/**
 * Réglages du moteur de prévision court terme modifiables depuis le back-office (26/10) : un jeu
 * pour tout le réseau, versionné en base (`forecast_engine_settings`). Point de vérité des bornes
 * que le schéma Vine du backend et le formulaire Zod de la modale appliquent tous deux. Sans
 * dépendance runtime : exporté par le barrel.
 */

export const FORECAST_ENGINE_SETTINGS_BOUNDS = {
  /** Jours retenus dans la vente moyenne journalière (VMJ). */
  vmjDays: { min: 3, max: 7 },
  /** Part de la VMJ week-end dans la prévision du samedi et du dimanche (0 : aucune, 1 : toute). */
  weekendVmjBlend: { min: 0, max: 1 },
  /** Poids du dernier jour complet retenu dans la VMJ ; `null` : moyenne simple. */
  lastDayWeight: { min: 0, max: 1 },
} as const

/** Motif obligatoire de chaque application, en caractères après `trim`. */
export const FORECAST_ENGINE_REASON_MIN_LENGTH = 3
export const FORECAST_ENGINE_REASON_MAX_LENGTH = 500

/** Statut d'une tâche de fond du moteur : aperçu, ou relance portée par la version qui l'a demandée. */
export const FORECAST_ENGINE_JOB_STATUSES = ['queued', 'running', 'succeeded', 'failed'] as const
export type ForecastEngineJobStatus = (typeof FORECAST_ENGINE_JOB_STATUSES)[number]
/** Statut de la relance de la dernière passe officielle, sur la version qui l'a demandée. */
export type ForecastEngineRerunStatus = ForecastEngineJobStatus
/** Statut d'un aperçu calculé en tâche de fond. */
export type ForecastEnginePreviewStatus = ForecastEngineJobStatus

/** Statuts d'une tâche encore en file ou en calcul. */
export const FORECAST_ENGINE_ACTIVE_STATUSES = [
  'queued',
  'running',
] as const satisfies readonly ForecastEngineJobStatus[]
export type ForecastEngineActiveStatus = (typeof FORECAST_ENGINE_ACTIVE_STATUSES)[number]

/** Aperçu ou relance encore en file ou en calcul. */
export const isForecastEngineJobActive = (
  status: ForecastEngineJobStatus | null | undefined,
): status is ForecastEngineActiveStatus => status === 'queued' || status === 'running'

/** Jours de l'aperçu, à partir du lendemain de la passe comparée. */
export const FORECAST_ENGINE_PREVIEW_DAYS = 7

export interface ForecastEngineSettingsValues {
  vmjDays: number
  weekendVmjBlend: number
  lastDayWeight: number | null
  /**
   * « Capping » (décision du 29/10) : `true`, la prévision part des ventes telles qu'observées,
   * sans correction des ruptures, pour tout le réseau ; `false`, de la demande corrigée des
   * ruptures. Hors des préréglages.
   */
  capping: boolean
}

/** « Capping » d'avant ce réglage, et des constantes du code : la correction des ruptures active. */
export const FORECAST_ENGINE_CAPPING_DEFAULT = false

/** Les trois réglages de la « Force de calcul », les seuls que fixe un préréglage. */
export type ForecastEnginePresetValues = Pick<
  ForecastEngineSettingsValues,
  'vmjDays' | 'weekendVmjBlend' | 'lastDayWeight'
>

/** Même « Force de calcul » : les trois réglages d'un préréglage, sans le « Capping ». */
export const sameEnginePresetValues = (
  a: ForecastEnginePresetValues,
  b: ForecastEnginePresetValues,
): boolean =>
  a.vmjDays === b.vmjDays &&
  a.weekendVmjBlend === b.weekendVmjBlend &&
  a.lastDayWeight === b.lastDayWeight

/**
 * Même version : tous les réglages, « Capping » compris. Une application identique à la version en
 * vigueur est refusée.
 */
export const sameEngineSettings = (
  a: ForecastEngineSettingsValues,
  b: ForecastEngineSettingsValues,
): boolean => sameEnginePresetValues(a, b) && a.capping === b.capping

/**
 * Préréglages du moteur (« Force de calcul », décision du 28/10), dans l'ordre du contrôle segmenté.
 * `custom` : les trois réglages libres. Le « Capping » n'en fait pas partie.
 */
export const FORECAST_ENGINE_PRESET_KEYS = ['prudente', 'normale', 'reactive', 'custom'] as const
export type ForecastEnginePreset = (typeof FORECAST_ENGINE_PRESET_KEYS)[number]
export type ForecastEngineFixedPreset = Exclude<ForecastEnginePreset, 'custom'>

/**
 * Valeurs de chaque préréglage : **seul endroit à modifier** pour les ajuster. Le backend refuse un
 * préréglage annoncé dont les valeurs diffèrent, et le formulaire de la modale les recopie.
 * - `normale` : les réglages de la prod au 28/10 (constantes du code, `DEFAULT_PARAMS`) ;
 * - `reactive` : VMJ sur 3 jours, dernier jour à 50 %, week-end calé sur la semaine en cours ;
 * - `prudente` : VMJ sur 5 jours en moyenne simple, week-end calé sur les week-ends passés.
 */
export const FORECAST_ENGINE_PRESETS = {
  prudente: { vmjDays: 5, lastDayWeight: null, weekendVmjBlend: 1 },
  normale: { vmjDays: 5, lastDayWeight: null, weekendVmjBlend: 1 / 3 },
  reactive: { vmjDays: 3, lastDayWeight: 0.5, weekendVmjBlend: 0 },
} as const satisfies Record<ForecastEngineFixedPreset, ForecastEnginePresetValues>

export const FORECAST_ENGINE_PRESET_LABELS: Record<ForecastEnginePreset, string> = {
  prudente: 'Prudente',
  normale: 'Normale',
  reactive: 'Réactive',
  custom: 'Personnalisé',
}

const FIXED_PRESET_KEYS = FORECAST_ENGINE_PRESET_KEYS.filter(
  (key): key is ForecastEngineFixedPreset => key !== 'custom',
)

/**
 * Préréglage de ces valeurs : les valeurs font foi, celles d'aucun préréglage sont `custom`. Un
 * préréglage choisi puis retouché redevient donc `custom`, et des curseurs ramenés sur les valeurs
 * d'un préréglage le retrouvent. Seuls les trois réglages de la « Force de calcul » comptent : le
 * « Capping » ne change pas le préréglage.
 */
export const forecastEnginePresetOf = (values: ForecastEnginePresetValues): ForecastEnginePreset =>
  FIXED_PRESET_KEYS.find((key) => sameEnginePresetValues(FORECAST_ENGINE_PRESETS[key], values)) ??
  'custom'
